/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import deepcopy from 'deepcopy';
import { FancyAction } from '@touchplan/common';
import { ICombinedEdit } from '@touchplan/common';
import { ListLike } from '@touchplan/common';
import { CombinedTicketTypes, TicketDropdownTypes } from '@touchplan/common';
import { Ticket, TicketRenderTypes } from '@touchplan/common';
import { TicketType } from '@touchplan/common';

/**
 * Quick and dirty sample usage
 * ```
 * ticketList.list$.subscribe(list => {
 * 	const group = GroupedTicket.createFromTickets(list);
 * 	logger.log('group', group);
 * });
 * ```
 */

export class GroupedTicket extends Ticket implements ICombinedEdit<Ticket> {
	public srcList: Map<string, Ticket> = new Map<string, Ticket>();
	// current iteration - always corresponds to the first item in srcList
	public selectedList: Map<string, Ticket> = new Map<string, Ticket>();
	//TODO - add assorted "type lists" here for multi-edit, task/ constraint/ the special temp types in old edit if necesary
	public lastEvent!: FancyAction<Ticket>;

	public constructor() {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		super(null, null as any);
	}

	//creator shorthand, mostly for stubbed version, undecided if this will survive long term
	//creates an instance and applies an update. Don't want to do that on the contructor
	public static createFromTickets(tickets: ListLike<Ticket>): GroupedTicket {
		const grouped = new GroupedTicket();
		grouped.updateFromTickets(tickets);
		return grouped;
	}

	public updateFromTickets(ticketList: ListLike<Ticket>): void {
		//in v1, just replace
		this.srcList.clear();
		this.selectedList.clear();
		let count = 0;
		ticketList.forEach((t, key) => {
			this.srcList.set(key, t);
			if (count === 0) {
				//v1 - selectedList will always be the first item
				this.selectedList.set(key, t);
			}
			count++;
		});

		// this.smashList(ticketList);
		this.smashList(this.selectedList);
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	private smashList(ticketList: ListLike<Ticket>): void {
		//when actually supporting multiple, this will need some fairly involved logic for comparing values, populating the metadata object etc
		//but for now... "copy" the primary data

		//if multiple tickets are passed in, the last will end up being used
		ticketList.forEach((ticket) => {
			for (const renderType of Object.values(TicketRenderTypes)) {
				this[renderType] = deepcopy(ticket[renderType]);
			}
			this.renderType = ticket.renderType;
			this.view = this[this.renderType];

			this.$id = ticket.$id;
		});
	}

	public isAnyConstraint(type: CombinedTicketTypes): boolean {
		return (
			type === TicketType.Constraint ||
			type === TicketDropdownTypes.CONSTRAINT_ACCEPTED ||
			type === TicketDropdownTypes.CONTRAINT_ASSIGNED
		);
	}
}
